
// Single source of truth for PointView's visual vertex/point semantics -
// the shared "visual language" every digitized geometry uses so a point on
// screen can be read at a glance as one of three distinct things:
//
//   ●  VERTEX         - an ordinary editing handle on a LineString/Polygon.
//                       The least dominant point: small, round.
//   ■  ENDPOINT       - the first or last vertex of an OPEN LineString.
//                       Structurally more important than an interior vertex,
//                       so bigger and square. Polygons are closed rings and
//                       have no endpoints in this sense - every polygon
//                       vertex stays a plain VERTEX.
//   ◎  POINT_FEATURE  - a standalone registered Point feature, not an
//                       editing handle at all. The most prominent: largest,
//                       a ring form.
//
// This module is pure (no DOM/THREE) and Level-1 unit-testable. FeatureVisual
// is a thin applier of what resolveVertexRole()/vertexRoleStyle() return - it
// must not re-derive "is this an endpoint" logic of its own, and no per-tool
// code may define its own vertex look (task: one centralised semantic, not
// three parallel special-cases).
//
// The type signal is carried PRIMARILY by form + size (VERTEX < ENDPOINT <
// POINT_FEATURE in on-screen prominence); colour stays owned by the existing
// feature-category / selection / hover system and is deliberately NOT used
// here as a third semantic axis.
//
// Forward-compatibility note: a future "shared/connected endpoint" role
// (◆) would slot in as one more VERTEX_ROLE entry + one more VERTEX_ROLE_STYLE
// row, consumed the same way - it is intentionally NOT implemented here yet
// (out of scope), the shape of this module just does not preclude it.

export const VERTEX_ROLE = Object.freeze({
	VERTEX: "vertex",
	ENDPOINT: "endpoint",
	POINT_FEATURE: "pointFeature",
});

// geometryType: "Point" | "LineString" | "Polygon"
// index:        0-based vertex index within the feature
// vertexCount:  total vertices currently in the feature
//
// A Polygon (closed ring) never yields ENDPOINT. A LineString's first AND
// last vertex are both ENDPOINT (a 2-vertex line is therefore two endpoints
// and no interior vertex; a still-being-digitized 1-vertex line's single
// vertex counts as an endpoint). Any out-of-range / unknown input degrades
// to a plain VERTEX rather than throwing.
export function resolveVertexRole(geometryType, index, vertexCount){
	if(geometryType === "Point") return VERTEX_ROLE.POINT_FEATURE;

	if(geometryType === "LineString"){
		if(index === 0 || index === vertexCount - 1) return VERTEX_ROLE.ENDPOINT;
		return VERTEX_ROLE.VERTEX;
	}

	// Polygon and everything else: an ordinary editing handle, no endpoints.
	return VERTEX_ROLE.VERTEX;
}

// Per-role visual style:
//   form       - which shared primitive FeatureVisual renders the marker with
//                ("sphere" | "box" | "ring")
//   pixels     - constant on-screen size (see ScreenSpaceScale.js) at rest
//   hoverPixels- constant on-screen size while hovered
//
// The exact numbers are tuned for readability against a real point cloud at
// normal camera distance. Now that each role has a clearly distinct FORM,
// size is kept deliberately restrained - the markers must not dominate the
// view. ENDPOINT and POINT_FEATURE are only marginally larger than an
// ordinary vertex (just enough to register as "more than a handle"); the
// type is carried by form, not by bulk. Ordering is still non-decreasing
// (vertex <= endpoint == point-feature) so size never contradicts the
// hierarchy.
export const VERTEX_ROLE_STYLE = Object.freeze({
	[VERTEX_ROLE.VERTEX]:        Object.freeze({form: "sphere", pixels: 6, hoverPixels: 8}),
	[VERTEX_ROLE.ENDPOINT]:      Object.freeze({form: "box",    pixels: 7, hoverPixels: 9}),
	[VERTEX_ROLE.POINT_FEATURE]: Object.freeze({form: "ring",   pixels: 7, hoverPixels: 9}),
});

export function vertexRoleStyle(role){
	return VERTEX_ROLE_STYLE[role] || VERTEX_ROLE_STYLE[VERTEX_ROLE.VERTEX];
}
